import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbar";
import { API_BASE_URL } from "../../../../config";
import { LinearProgress } from "../../rcAutoDetails2/style";

// dopo la quotazione/salvataggio si interroga subMissionGetState finche' i
// dati della targa non sono disponibili: idStato === 1 significa "pronti"
const RECUPERO_TARGA_STATO_PRONTO = 1;
const RECUPERO_TARGA_INTERVALLO_MS = 10000;
const RECUPERO_TARGA_MAX_TENTATIVI = 3;
const RECUPERO_TARGA_DURATA_BARRA_MS = 15000;
const RECUPERO_TARGA_MESSAGGIO = [
  "Le informazioni associate alla targa verranno visualizzate automaticamente non appena disponibili",
  "Solitamente l'operazione richiede circa 10 secondi.",
];

// barra che si riempie in `durataMs`; per farla ripartire da zero basta
// cambiarle la `key`
const RecuperoTargaProgress = ({ durataMs }) => {
  const [trascorsi, setTrascorsi] = useState(0);

  useEffect(() => {
    const inizio = Date.now();
    const timer = setInterval(() => {
      const ms = Math.min(Date.now() - inizio, durataMs);
      setTrascorsi(ms);
      if (ms >= durataMs) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [durataMs]);

  return (
    <Box width="100%" display="flex" flexDirection="column" gap={1}>
      <LinearProgress
        variant="determinate"
        value={(trascorsi / durataMs) * 100}
      />
      <Typography variant="body2" align="center" color="text.secondary">
        Ricerca in corso...
      </Typography>
    </Box>
  );
};

// modale bloccante: niente X, niente chiusura con click fuori o ESC
export const RecuperoTargaDialog = ({ open, tentativo }) => (
  <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
    <DialogContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        py: 4,
      }}
    >
      <CircularProgress />
      <Typography variant="h3" align="center" fontWeight="bold">
        Stiamo recuperando i dati del veicolo
      </Typography>
      <Box px={12} gap={2} display="flex" flexDirection="column">
        <Typography variant="body1" align="center">
          {RECUPERO_TARGA_MESSAGGIO[0]}
        </Typography>
        <Typography variant="body1" align="center">
          {RECUPERO_TARGA_MESSAGGIO[1]}
        </Typography>
      </Box>
      <RecuperoTargaProgress
        key={tentativo}
        durataMs={RECUPERO_TARGA_DURATA_BARRA_MS}
      />
    </DialogContent>
  </Dialog>
);

// esiti di attendiRecuperoTarga
export const ESITO_RECUPERO_TARGA = {
  PRONTO: "pronto", // idStato === 1: i dati della targa ci sono
  ESAURITO: "esaurito", // tentativi finiti senza dati
  ERRORE: "errore", // subMissionGetState ha risposto con errore (snackbar gia' mostrata)
  ANNULLATO: "annullato", // componente smontato durante l'attesa
};

export const useRecuperoTarga = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [recuperoTarga, setRecuperoTarga] = useState(false);
  const [tentativoRecupero, setTentativoRecupero] = useState(1);
  const pollTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const attendi = (ms) =>
    new Promise((resolve) => {
      pollTimeoutRef.current = setTimeout(resolve, ms);
    });

  // ritorna il `data` della risposta quando status === 1, altrimenti null
  async function subMissionGetState(idUid) {
    const token = sessionStorage.getItem("token");
    try {
      const response = await fetch(`${API_BASE_URL}/api/subMissionGetState/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({ idUid }),
      });

      const responseData = await response.json();

      if (responseData.status === 1) {
        return responseData.data ?? {};
      }
      if (responseData.status === 0) {
        dispatch(
          showSnackbar({ message: responseData.message, severity: "error" }),
        );
      } else if (responseData.status === -1) {
        navigate("/auth/sign-in");
      }
      return null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  // mostra la modale e interroga subMissionGetState fino a 3 volte, una ogni
  // 10 secondi, finche' i dati della targa non sono disponibili
  async function attendiRecuperoTarga(idUid) {
    setRecuperoTarga(true);
    try {
      for (
        let tentativo = 1;
        tentativo <= RECUPERO_TARGA_MAX_TENTATIVI;
        tentativo++
      ) {
        setTentativoRecupero(tentativo);
        const stato = await subMissionGetState(idUid);

        if (!stato) {
          return ESITO_RECUPERO_TARGA.ERRORE;
        }
        if (stato.idStato === RECUPERO_TARGA_STATO_PRONTO) {
          return ESITO_RECUPERO_TARGA.PRONTO;
        }

        if (tentativo < RECUPERO_TARGA_MAX_TENTATIVI) {
          await attendi(RECUPERO_TARGA_INTERVALLO_MS);
          if (!isMountedRef.current) {
            return ESITO_RECUPERO_TARGA.ANNULLATO;
          }
        }
      }
      return ESITO_RECUPERO_TARGA.ESAURITO;
    } finally {
      if (isMountedRef.current) {
        setRecuperoTarga(false);
      }
    }
  }

  return { recuperoTarga, tentativoRecupero, attendiRecuperoTarga };
};
